import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Contact = () => {
  const { t, language } = useLanguage();

  const seo = {
    fr: {
      title: "Contact Henry & Gaby – Nous contacter",
      description: "Contactez Henry & Gaby pour rejoindre notre club privé, organiser un événement exclusif ou obtenir des informations sur nos services d'aviation privée et d'hospitalité.",
      h1: "Contactez Henry & Gaby"
    },
    en: {
      title: "Contact Henry & Gaby – Get in Touch",
      description: "Contact Henry & Gaby to join our private club, organize an exclusive event or get information about our private aviation and hospitality services.",
      h1: "Contact Henry & Gaby"
    }
  };

  const currentSeo = seo[language] || seo.fr;

  return (
    <div>
      <Helmet>
        <title>{currentSeo.title}</title>
        <meta name="description" content={currentSeo.description} />
        <link rel="canonical" href="https://henry-and-gaby.com/contact" />
        <meta property="og:title" content={currentSeo.title} />
        <meta property="og:description" content={currentSeo.description} />
        <meta property="og:url" content="https://henry-and-gaby.com/contact" />
      </Helmet>

      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 className="hero-large" style={{ marginBottom: '16px' }}>
            {currentSeo.h1}
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            {t.contact.subtitle}
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="section-padding">
        <div className="container">
          <div className="grid-two-column">
            {/* Contact Details */}
            <div>
              <h2 className="hero-medium" style={{ marginBottom: '32px' }}>
                JSX International & Henry & Gaby
              </h2>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '32px'
              }}>
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid var(--border-light)',
                      background: 'var(--bg-secondary)',
                      flexShrink: 0
                    }}>
                      <MapPin size={20} color="var(--text-primary)" />
                    </div>
                    <div>
                      <h3 className="heading-3" style={{ marginBottom: '12px' }}>Address</h3>
                      <div style={{ marginBottom: '16px' }}>
                        <p className="body-regular" style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: '4px' }}>
                          Address USA:
                        </p>
                        <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                          996 Maine Ave Sw, Ste 733<br />
                          Washington, District of Columbia, 20024, United States
                        </p>
                      </div>
                      <div>
                        <p className="body-regular" style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: '4px' }}>
                          Address France:
                        </p>
                        <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                          78, Avenue des Champs-Élysées<br />
                          75008 Paris
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid var(--border-light)',
                      background: 'var(--bg-secondary)',
                      flexShrink: 0
                    }}>
                      <Phone size={20} color="var(--text-primary)" />
                    </div>
                    <div>
                      <h3 className="heading-3" style={{ marginBottom: '8px' }}>Phone</h3>
                      <div style={{ marginBottom: '12px' }}>
                        <p className="body-regular" style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: '4px' }}>
                          Phone France :
                        </p>
                        <a
                          href="tel:+33641664258"
                          className="body-regular"
                          style={{
                            color: 'var(--text-secondary)',
                            textDecoration: 'none',
                            transition: 'color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'}
                          onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
                        >
                          +33 6 41 66 42 58
                        </a>
                      </div>
                      <div>
                        <p className="body-regular" style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: '4px' }}>
                          Phone USA :
                        </p>
                        <a
                          href="tel:+12025064111"
                          className="body-regular"
                          style={{
                            color: 'var(--text-secondary)',
                            textDecoration: 'none',
                            transition: 'color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'}
                          onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
                        >
                          +1 202-506-4111
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid var(--border-light)',
                      background: 'var(--bg-secondary)',
                      flexShrink: 0
                    }}>
                      <Mail size={20} color="var(--text-primary)" />
                    </div>
                    <div>
                      <h3 className="heading-3" style={{ marginBottom: '8px' }}>Email</h3>
                      <a
                        href="mailto:contact@henry-and-gaby.com"
                        className="body-regular"
                        style={{
                          color: 'var(--text-secondary)',
                          textDecoration: 'none',
                          transition: 'color 0.2s ease',
                          wordBreak: 'break-word'
                        }}
                        onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'}
                        onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
                      >
                        contact@henry-and-gaby.com
                      </a>
                    </div>
                  </div>
                </div>

                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid var(--border-light)',
                      background: 'var(--bg-secondary)',
                      flexShrink: 0
                    }}>
                      <Clock size={20} color="var(--text-primary)" />
                    </div>
                    <div>
                      <h3 className="heading-3" style={{ marginBottom: '8px' }}>Hours</h3>
                      <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                        {t.contact.hours}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Image */}
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_global-advisory-1/artifacts/gtq34k0b_page%20contact.png"
                alt="Business meeting"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  minHeight: '500px'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <p className="hero-medium" style={{
            fontStyle: 'italic',
            color: 'var(--text-secondary)',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            "{t.contact.quote}"
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;